import { formatMarkdown } from "@/components/formatMarkdown";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import Link from "next/link";

interface FAQProps {
  question: string;
  answer: string;
}

export const faqs: FAQProps[] = [
  {
    question: "How is this different from Heroku/Vercel/Netlify?",
    answer:
      "Platforms like Heroku/Vercel often with usage limitations and higher costs. We provide a platform to deploy and manage apps *on your own server*. You bring a cost-effective VPS (like DigitalOcean, Hetzner, Verda Cloud), connect it, and we handle the deployment, HTTPS, updates, and backups. You get full control and predictable pricing without the complexity."
  },
  {
    // Question about natively running the apps
    question: "Can I run my apps natively on my own VPS?",
    answer:
      "Yes! DollarDeploy runs your apps such as React, NextJS or Go natively as a systemd process. So there is no overhead of Docker and Docker Registry, we just build your app and deliver it to your host. Additionally we use OS packages of Redis and Postgres so these also run natively at full speed."
  },
  {
    question: "Is it really 'one-click' deployment?",
    answer:
      "Pretty much! After connecting your server and Git repository (GitHub, GitLab, etc.), deploying is often just a single click or an automatic trigger on `git push`. We automate the build process, deployment, and necessary configurations like setting up HTTPS."
  },
  {
    question: "What kind of apps can I deploy?",
    answer:
      "We're built for modern web developers. You can deploy Next.js, React, React Static, Python apps, natively built apps (e.g. Go or Rust), and pretty much any other app using Docker Compose, static sites, etc. If it runs with Docker, you can deploy it."
  },
  {
    question: "Can I deploy AI models like Llama?",
    answer:
      "Yes! Our platform simplifies self-hosting AI applications. You can easily deploy **Large Language Models (LLMs)**, and other AI tools using GPU-enabled servers from **Verda Cloud (formerly DataCrunch)** for optimal performance, which you can easily provision on our platform."
  },
  {
    question: "Which VPS providers are supported?",
    answer:
      "You can connect almost any Linux server with SSH access. We have seamless integration guides for popular providers like **DigitalOcean, Hetzner, and Verda Cloud (formerly DataCrunch)**, but any cloud VPS, bare-metal server, or even a Raspberry Pi can work."
  },
  {
    question: "How are HTTPS/SSL certificates handled?",
    answer:
      "Automatically! Once you point your domain to your server and add it in our dashboard, we automatically obtain and renew free **Let's Encrypt SSL certificates** for your applications."
  },
  {
    question: "How do backups work?",
    answer:
      "Effortlessly. You can create **one-click backups** for your database, and application data directly from the dashboard. "
  },
  {
    question: "Do I need to manage server updates myself?",
    answer:
      "We handle the application deployment and related configurations. Server security updates are generally still the user's responsibility, but we automate it so it just a single click in the Dashboard."
  },
  {
    question: "Is my data secure? Who has access?",
    answer:
      "Your application code, data, and server remain under your control on your server. We only need SSH access to manage deployments and configurations. Sensitive credentials like SSH keys and API tokens are encrypted at rest in our database."
  }
];

export const FAQ = ({ limit }: { limit?: number }) => {
  const list = limit !== undefined ? faqs.slice(0, limit) : faqs;

  return (
    <section id="faq" className="container">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        Frequently Asked <span className="gradient-light dark:gradient-dark">Questions</span>
      </h2>

      <Accordion type="single" collapsible className="w-full AccordionRoot">
        {list.map(({ question, answer }, index) => (
          <AccordionItem key={index} value={String(index)}>
            <AccordionTrigger className="text-left font-semibold">{question}</AccordionTrigger>

            <AccordionContent className="text-base">{formatMarkdown(answer)}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="flex md:flex-row flex-col gap-4 justify-between items-center mt-8">
        {limit !== undefined ? (
          <Link
            href="/faq"
            className="text-primary transition-all border-primary hover:border-b-2 font-medium"
          >
            See all the FAQs →
          </Link>
        ) : (
          <span />
        )}

        <h3 className="font-medium">
          Still have questions?{" "}
          <a
            href="mailto:hello@dollardeploy.com"
            className="text-primary transition-all border-primary hover:border-b-2"
          >
            Contact us
          </a>{" "}
          or chat with us on{" "}
          <a href="https://discord.gg/BHbfmaAGb7" target="_blank" rel="noreferrer noopener">
            Discord
          </a>
          .
        </h3>
      </div>
    </section>
  );
};
